import React from 'react';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Helmet } from 'react-helmet-async';
import { AlertTriangle, Scale, FileText, ExternalLink, Shield, Mail, Gavel } from 'lucide-react';

export default function DisclaimerPage() {
  const effectiveDate = "January 12, 2026";

  return (
    <PublicLayout>
      <Helmet>
        <title>Professional Services Disclaimer | Kosha Tax Consultants</title>
        <meta name="description" content="Important legal disclaimers regarding the use of Kosha Tax Consultants' tax and accounting services." />
      </Helmet>

      <div className="bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold text-center mb-4 text-foreground">Professional Services Disclaimer</h1>
            <p className="text-center text-muted-foreground mb-8">Effective Date: {effectiveDate}</p>
            
            <Card className="shadow-lg">
              <CardContent className="prose prose-lg dark:prose-invert max-w-none p-6 md:p-8">
                
                {/* Introduction */}
                <section className="mb-8">
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 not-prose mb-4">
                    <p className="text-blue-800 dark:text-blue-200">
                      <strong>Kosha Tax Consultants</strong> is a trade name (DBA) of <strong>Annapurna Events LLC</strong>, 
                      a Texas limited liability company (&quot;Company,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;).
                    </p>
                  </div>
                  <p>
                    Information provided on this website may include general informational content. Paid services 
                    provided by Kosha Tax Consultants involve tax preparation and administrative support based on 
                    information supplied by the user. All information is provided in good faith; however, we make 
                    no representations or warranties of any kind, express or implied, regarding accuracy, completeness, 
                    reliability, availability, or suitability of the information.
                  </p>
                </section>

                {/* Not Legal, Financial, or Tax Advice */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Scale className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Not Legal, Financial, or Tax Advice</h2>
                  </div>
                  <p>
                    The content on this website and the services we provide do not constitute legal, financial, 
                    investment, or professional advice. Tax laws are complex and subject to change. Information 
                    provided should not be used as a substitute for consultation with qualified tax, legal, or 
                    financial professionals who are familiar with your specific circumstances.
                  </p>
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 not-prose">
                    <p className="text-amber-800 dark:text-amber-200 text-sm">
                      <strong>Important:</strong> You are encouraged to consult with a qualified professional before 
                      making decisions or taking actions based on information provided through our services.
                    </p>
                  </div>
                </section>

                {/* Tax Preparation Services */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Tax Preparation Services – No Guarantee of Results</h2>
                  </div>
                  <p>
                    While we strive to prepare accurate tax filings based on the information you provide, we do not 
                    guarantee specific outcomes, including but not limited to:
                  </p>
                  <ul>
                    <li>The amount of any tax refund</li>
                    <li>Reduction of tax liability</li>
                    <li>Acceptance of any filing by the IRS or state tax authorities</li>
                    <li>Avoidance of audits, penalties, or examinations</li>
                  </ul>
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 not-prose">
                    <p className="text-red-800 dark:text-red-200 text-sm">
                      <strong>Your Responsibility:</strong> You are solely responsible for the accuracy and completeness 
                      of all information and documents you provide. Errors, omissions, or misrepresentations may result 
                      in penalties, interest, or additional taxes assessed by tax authorities.
                    </p>
                  </div>
                </section>

                {/* IRS Disclosure */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Gavel className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">IRS Disclosure</h2>
                  </div>
                  <div className="bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg p-4 not-prose">
                    <p className="text-slate-700 dark:text-slate-300 text-sm">
                      Any U.S. federal tax information provided through this website or our services is not intended 
                      or written to be used, and cannot be used, for the purpose of avoiding penalties under the 
                      Internal Revenue Code or promoting, marketing, or recommending any tax-related transaction.
                    </p>
                  </div>
                </section>

                {/* No Guarantee of Results */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">No Guarantee of Results</h2>
                  </div>
                  <p>
                    Past results, examples, or client testimonials do not guarantee future outcomes. Each tax situation 
                    is unique, and results will vary depending on individual facts and circumstances.
                  </p>
                </section>

                {/* Third-Party Links */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <ExternalLink className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Third-Party Links</h2>
                  </div>
                  <p>
                    Our website may contain links to third-party websites or services that are not owned or controlled 
                    by the Company. We assume no responsibility for the content, privacy policies, or practices of any 
                    third-party websites or services. Accessing third-party links is at your own risk.
                  </p>
                </section>

                {/* Limitation of Liability */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Limitation of Liability</h2>
                  </div>
                  <p>
                    To the maximum extent permitted by applicable law, Annapurna Events LLC and its officers, directors, 
                    employees, and agents shall not be liable for any direct, indirect, incidental, special, consequential, 
                    or exemplary damages arising out of or related to your use of our website or services.
                  </p>
                  <p>
                    Our total liability for any claim arising from or related to the services shall not exceed the amount 
                    paid by you for the specific service giving rise to the claim.
                  </p>
                </section>

                {/* Indemnification */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold">Indemnification</h2>
                  <p>
                    You agree to indemnify and hold harmless Kosha Tax Consultants, Annapurna Events LLC, and their 
                    respective officers, directors, employees, and agents from and against any claims, liabilities, 
                    damages, losses, and expenses arising from your use of the services or violation of applicable 
                    terms or laws.
                  </p>
                </section>

                {/* Changes to This Disclaimer */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold">Changes to This Disclaimer</h2>
                  <p>
                    We reserve the right to modify this Disclaimer at any time. Changes will be effective upon posting. 
                    Continued use of the website or services after changes constitutes acceptance of the revised Disclaimer.
                  </p>
                </section>

                {/* Contact Information */}
                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Contact Us</h2>
                  </div>
                  <p>For questions regarding this Disclaimer, contact:</p>
                  <div className="bg-muted/50 p-4 rounded-lg not-prose">
                    <p className="font-medium">Kosha Tax Consultants</p>
                    <p className="text-muted-foreground">A DBA of Annapurna Events LLC (Texas)</p>
                    <p className="text-muted-foreground">Email: help@koshatax.com</p>
                  </div>
                </section>

              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
